import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Copy, Gauge, Pause, Pencil, Play, Plus, RefreshCcw, RotateCcw,
  TestTube2, Trash2, X,
} from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";


const samplePayload = `{
  "pk": "263075161",
  "username": "ferfernandaguimaraes_",
  "full_name": "Fernanda Guimarães",
  "is_verified": false,
  "profile_pic_url": "https://s3.us-southeast-1.idrivee2.com/...",
  "source_target_id": 263075161,
  "captured_at": "2026-08-05T17:00:00Z"
}`;


const blankForm = () => ({ id: null, name: "", url: "", enabled: true, headers: [{ key: "", value: "" }] });


export default function OutboundPage() {
  const queryClient = useQueryClient();
  const [form, setForm] = useState(blankForm());
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [rate, setRate] = useState(5);
  const rateTimer = useRef(null);
  const { data: state } = useQuery({
    queryKey: ["outbound-state"],
    queryFn: () => api.get("/outbound/state").then((response) => response.data),
    refetchInterval: 1000,
  });
  const { data: endpoints } = useQuery({
    queryKey: ["outbound-endpoints"],
    queryFn: () => api.get("/outbound/endpoints").then((response) => response.data.items),
    refetchInterval: 2000,
  });
  useEffect(() => {
    if (state?.requests_per_second && !rateTimer.current) setRate(Number(state.requests_per_second));
  }, [state?.requests_per_second]);
  useEffect(() => () => rateTimer.current && window.clearTimeout(rateTimer.current), []);

  const control = useMutation({
    mutationFn: (action) => api.post(`/outbound/control/${action}`),
    onSuccess: (_, action) => {
      toast.success(`Comando ${action} aplicado.`);
      queryClient.invalidateQueries({ queryKey: ["outbound-state"] });
      queryClient.invalidateQueries({ queryKey: ["outbound-endpoints"] });
    },
    onError: (error) => toast.error(errorMessage(error)),
  });
  const active = ["running", "waiting_data"].includes(state?.status);

  const updateRate = ([value]) => {
    setRate(value);
    if (rateTimer.current) window.clearTimeout(rateTimer.current);
    rateTimer.current = window.setTimeout(async () => {
      try {
        await api.put("/outbound/rate", { requests_per_second: value });
        queryClient.setQueryData(["outbound-state"], (current) => current ? { ...current, requests_per_second: value } : current);
      } catch (error) {
        toast.error(errorMessage(error));
      } finally {
        rateTimer.current = null;
      }
    }, 150);
  };
  const openCreate = () => { setForm(blankForm()); setDialogOpen(true); };
  const openEdit = (endpoint) => {
    const headers = Object.entries(endpoint.headers || {}).map(([key, value]) => ({ key, value }));
    setForm({ id: endpoint.id, name: endpoint.name, url: endpoint.url, enabled: endpoint.enabled, headers: headers.length ? headers : [{ key: "", value: "" }] });
    setDialogOpen(true);
  };
  const changeHeader = (index, field, value) => setForm((current) => ({
    ...current,
    headers: current.headers.map((header, position) => position === index ? { ...header, [field]: value } : header),
  }));
  const saveEndpoint = async () => {
    setSaving(true);
    const headers = Object.fromEntries(form.headers.filter((header) => header.key.trim()).map((header) => [header.key.trim(), header.value]));
    const payload = { name: form.name, url: form.url, enabled: form.enabled, headers };
    try {
      if (form.id) await api.put(`/outbound/endpoints/${form.id}`, payload);
      else await api.post("/outbound/endpoints", payload);
      toast.success(form.id ? "API atualizada." : "API adicionada.");
      setDialogOpen(false);
      queryClient.invalidateQueries({ queryKey: ["outbound-endpoints"] });
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setSaving(false);
    }
  };
  const toggleEndpoint = async (endpoint, enabled) => {
    try {
      await api.put(`/outbound/endpoints/${endpoint.id}`, { ...endpoint, enabled, headers: endpoint.headers });
      queryClient.invalidateQueries({ queryKey: ["outbound-endpoints"] });
    } catch (error) { toast.error(errorMessage(error)); }
  };
  const testEndpoint = async (endpoint) => {
    try {
      const { data } = await api.post(`/outbound/endpoints/${endpoint.id}/test`);
      if (data.success) toast.success(`Teste concluído: HTTP ${data.status_code} em ${data.latency_ms}ms`);
      else toast.error(`Teste falhou: ${data.status_code || data.response}`);
    } catch (error) { toast.error(errorMessage(error)); }
  };
  const removeEndpoint = async (endpoint) => {
    try {
      await api.delete(`/outbound/endpoints/${endpoint.id}`);
      queryClient.invalidateQueries({ queryKey: ["outbound-endpoints"] });
    } catch (error) { toast.error(errorMessage(error)); }
  };
  const copySample = async () => {
    await navigator.clipboard.writeText(samplePayload);
    toast.success("JSON de exemplo copiado.");
  };

  return <div className="page">
    <section className="bot-command-bar" data-testid="outbound-command-bar">
      <Button className="command start" onClick={() => control.mutate("start")} disabled={active} data-testid="outbound-start-button"><Play size={17} />Iniciar</Button>
      <Button className="command pause" onClick={() => control.mutate("pause")} disabled={!active} data-testid="outbound-pause-button"><Pause size={17} />Pausar</Button>
      <Button className="command resume" onClick={() => control.mutate("resume")} disabled={active} data-testid="outbound-resume-button"><RefreshCcw size={17} />Continuar</Button>
      <Button className="command restart" onClick={() => control.mutate("restart")} data-testid="outbound-restart-button"><RotateCcw size={17} />Reiniciar</Button>
      <span className="command-status"><StatusBadge status={state?.status || "idle"} testId="outbound-status" /></span>
    </section>

    <section className="rate-panel" data-testid="outbound-rate-panel">
      <div className="rate-heading"><span><Gauge size={18} />Velocidade global de envio</span><strong data-testid="outbound-rate-value">{rate.toLocaleString("pt-BR")} req/s</strong></div>
      <Slider value={[rate]} min={1} max={5000} step={1} onValueChange={updateRate} data-testid="outbound-rate-slider" aria-label="Envios globais por segundo" />
      <div className="rate-scale"><span>1 req/s</span><span data-testid="outbound-in-flight">{Number(state?.in_flight || 0).toLocaleString("pt-BR")} em andamento</span><span>5.000 req/s</span></div>
    </section>

    <section className="outbound-progress-panel" data-testid="outbound-progress-panel">
      <div className="outbound-progress-heading"><span>Progresso total</span><strong data-testid="outbound-progress-percent">{Number(state?.progress_percent || 0).toFixed(1)}%</strong></div>
      <div className="progress-line"><span style={{ width: `${Math.min(100, Number(state?.progress_percent || 0))}%` }} /></div>
      <div className="outbound-progress-stats"><span data-testid="outbound-progress-completed">{Number(state?.completed_deliveries || 0).toLocaleString("pt-BR")} concluídos</span><span data-testid="outbound-progress-total">{Number(state?.expected_deliveries || 0).toLocaleString("pt-BR")} envios totais</span><span data-testid="outbound-progress-profiles">{Number(state?.profiles_total || 0).toLocaleString("pt-BR")} perfis</span></div>
      {state?.status === "waiting_data" && <p className="outbound-waiting-data" data-testid="outbound-waiting-data">Todos os perfis atuais foram enviados. O envio permanece ativo aguardando novos perfis.</p>}
    </section>

    <section className="outbound-section" data-testid="outbound-endpoints-section">
      <div className="outbound-section-heading"><div><span className="eyebrow">APIS EXTERNAS</span><strong>{state?.active_endpoint_count || 0} ativas</strong></div><Button onClick={openCreate} data-testid="outbound-add-endpoint-button"><Plus size={16} />Adicionar API</Button></div>
      <div className="outbound-list">
        {endpoints?.length ? endpoints.map((endpoint) => <article className="outbound-row" key={endpoint.id} data-testid={`outbound-endpoint-${endpoint.id}`}>
          <div className="outbound-identity"><strong>{endpoint.name}</strong><code>{endpoint.url}</code><small>POST • {Object.keys(endpoint.headers || {}).length} headers</small></div>
          <StatusBadge status={endpoint.status || "idle"} testId={`outbound-endpoint-status-${endpoint.id}`} />
          <div className="outbound-counts"><span><b>{Number(endpoint.sent_count || 0).toLocaleString("pt-BR")}</b> enviados</span><span><b>{Number(endpoint.ignored_count || 0).toLocaleString("pt-BR")}</b> ignorados</span><span><b>{Number(endpoint.error_count || 0).toLocaleString("pt-BR")}</b> erros</span></div>
          <Switch checked={endpoint.enabled} onCheckedChange={(value) => toggleEndpoint(endpoint, value)} data-testid={`outbound-endpoint-toggle-${endpoint.id}`} aria-label="Ativar API" />
          <div className="outbound-actions"><Button size="icon" variant="ghost" onClick={() => testEndpoint(endpoint)} data-testid={`outbound-test-${endpoint.id}`} title="Testar API"><TestTube2 size={16} /></Button><Button size="icon" variant="ghost" onClick={() => openEdit(endpoint)} data-testid={`outbound-edit-${endpoint.id}`} title="Editar API"><Pencil size={16} /></Button><Button size="icon" variant="ghost" onClick={() => removeEndpoint(endpoint)} data-testid={`outbound-delete-${endpoint.id}`} title="Excluir API"><Trash2 size={16} /></Button></div>
          {endpoint.last_error && <p className="outbound-error">{endpoint.last_error}{endpoint.next_attempt_at ? ` • retry ${new Date(endpoint.next_attempt_at).toLocaleTimeString("pt-BR")}` : ""}</p>}
        </article>) : <p className="table-empty">Nenhuma API externa configurada.</p>}
      </div>
    </section>

    <section className="outbound-section sample-section" data-testid="outbound-sample-section">
      <div className="outbound-section-heading"><div><span className="eyebrow">JSON ENVIADO</span><strong>Um perfil por requisição</strong></div><Button variant="outline" onClick={copySample} data-testid="outbound-copy-json-button"><Copy size={16} />Copiar JSON</Button></div>
      <pre data-testid="outbound-sample-json">{samplePayload}</pre>
    </section>

    <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
      <DialogContent className="outbound-dialog" data-testid="outbound-endpoint-dialog"><DialogHeader><DialogTitle>{form.id ? "Editar API" : "Adicionar API"}</DialogTitle><DialogDescription className="sr-only">Configuração da API externa.</DialogDescription></DialogHeader>
        <div className="field"><Label htmlFor="endpoint-name">Nome</Label><Input id="endpoint-name" value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} placeholder="Ex.: CRM principal" data-testid="outbound-name-input" /></div>
        <div className="field"><Label htmlFor="endpoint-url">URL do endpoint POST</Label><Input id="endpoint-url" value={form.url} onChange={(event) => setForm({ ...form, url: event.target.value })} placeholder="https://api.exemplo.com/perfis" data-testid="outbound-url-input" /></div>
        <div className="outbound-enabled"><Label htmlFor="endpoint-enabled">API ativa</Label><Switch id="endpoint-enabled" checked={form.enabled} onCheckedChange={(value) => setForm({ ...form, enabled: value })} data-testid="outbound-enabled-switch" /></div>
        <div className="headers-editor"><div className="headers-title"><Label>Headers</Label><Button type="button" size="sm" variant="outline" onClick={() => setForm({ ...form, headers: [...form.headers, { key: "", value: "" }] })} data-testid="outbound-add-header-button"><Plus size={14} />Header</Button></div>{form.headers.map((header, index) => <div className="header-row" key={index}><Input value={header.key} onChange={(event) => changeHeader(index, "key", event.target.value)} placeholder="X-API-Key" data-testid={`outbound-header-key-${index}`} /><Input value={header.value} onChange={(event) => changeHeader(index, "value", event.target.value)} placeholder="Valor" data-testid={`outbound-header-value-${index}`} /><Button type="button" size="icon" variant="ghost" onClick={() => setForm({ ...form, headers: form.headers.filter((_, position) => position !== index) })} data-testid={`outbound-remove-header-${index}`}><X size={15} /></Button></div>)}</div>
        <DialogFooter><Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button><Button onClick={saveEndpoint} disabled={saving || !form.name || !form.url} data-testid="outbound-save-endpoint-button">{saving ? "Salvando..." : "Salvar API"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  </div>;
}